import React, { useEffect, useState } from 'react';
import moment from 'moment';
import DocStatusTag from '_components/utils/DocStatusTag';
import PdfDocument from '../PdfDocument';
import { getFieldErrors, getLayoffStatus } from '../useLayoff';
import { useSavedSignature } from '../Signatures/useSignatures';
import {
  FieldError,
  RadioGroup,
  RequiredMark,
  SignatureField,
  TextField,
  firstErrors,
} from '../FormControls';
import { useNotice, useNoticePreview, useSignNotice } from './useNotice';
import styles from '../layoff.module.scss';

const EMPTY_FORM = {
  mailing_address_line1: '',
  mailing_address_line2: '',
  phone: '',
  returning_next_year: '',
  available_off_season: '',
  insurance: '',
  insurance_cancel_date: '',
  final_check: 'direct_deposit',
  workplace_injuries: '',
  injury_description: '',
  additional_notes: '',
};

const YES_NO_UNKNOWN = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'unknown', label: 'Unknown' },
];

const formatDate = (value) =>
  value ? moment(value).format('MMM D, YYYY') : '';

/**
 * Client-side checks that mirror SignLayoffNoticeRequest
 */
const validate = ({ form, signature, acknowledge }) => {
  const errors = {};
  if (!form.mailing_address_line1.trim())
    errors['form.mailing_address_line1'] = 'Enter your mailing address.';
  if (!form.mailing_address_line2.trim())
    errors['form.mailing_address_line2'] = 'Enter your city, state and ZIP.';
  if (!form.phone.trim()) errors['form.phone'] = 'Enter your phone number.';
  if (!form.additional_notes.trim())
    errors['form.additional_notes'] =
      'Add any notes or comments, or write "None".';
  if (!form.returning_next_year)
    errors['form.returning_next_year'] =
      'Choose whether you plan to return next year.';
  if (!form.available_off_season)
    errors['form.available_off_season'] =
      'Choose whether you are available off season.';
  if (!form.insurance)
    errors['form.insurance'] = 'Choose what should happen with your insurance.';
  if (form.insurance === 'cancel' && !form.insurance_cancel_date)
    errors['form.insurance_cancel_date'] =
      'Choose the date to cancel your insurance.';
  if (!form.final_check)
    errors['form.final_check'] =
      'Choose how you want to receive your final check.';
  if (!form.workplace_injuries)
    errors['form.workplace_injuries'] =
      'Tell us whether you have any workplace injuries to report.';
  if (form.workplace_injuries === 'yes' && !form.injury_description.trim())
    errors['form.injury_description'] = 'Describe the injury.';
  if (!signature) errors.signature = 'Add your signature.';
  if (!acknowledge)
    errors.acknowledge =
      'Tick the box to confirm you have read and received the notice.';
  return errors;
};

const openPdf = (file) => {
  const url = URL.createObjectURL(file);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
};

/**
 * The employee's lay-off notice. While it waits for them, they complete
 * "Confirm with employee" and sign; `onDone` is called from the confirmation screen.
 */
const EmployeeNotice = ({ hash, onDone }) => {
  const { data: notice, isLoading, isError } = useNotice(hash);
  const { data: savedSignature } = useSavedSignature();

  const [form, setForm] = useState(EMPTY_FORM);
  const [signature, setSignature] = useState('');
  const [acknowledge, setAcknowledge] = useState(false);
  const [errors, setErrors] = useState({});
  const [tried, setTried] = useState(false);
  const [draft, setDraft] = useState(null);
  const [signed, setSigned] = useState(null);

  const canSign = !!notice?.can?.sign && !signed;

  useEffect(() => {
    if (!canSign) {
      return undefined;
    }
    const timer = setTimeout(
      () => setDraft({ form, signature: signature || null }),
      1000,
    );
    return () => clearTimeout(timer);
  }, [canSign, form, signature]);

  const preview = useNoticePreview(
    hash,
    canSign ? draft : null,
    !!notice && (!canSign || !!draft),
  );

  const values = { form, signature, acknowledge };
  const shownErrors = { ...errors, ...(tried ? validate(values) : {}) };

  const sign = useSignNotice({
    onSuccess: (response) => setSigned(response.data),
    onError: (err) => setErrors(firstErrors(getFieldErrors(err))),
  });

  const setField = (field) => (value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [`form.${field}`]: undefined }));
  };

  const submit = () => {
    setTried(true);
    if (Object.keys(validate(values)).length) {
      return;
    }
    sign.mutate({ hash, form, signature, acknowledge });
  };

  if (isLoading) {
    return (
      <div className={styles.page_message}>
        <img
          alt='Loading'
          src={require('../../assets/images/loader.gif').default}
        />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.page_message}>
        This lay-off notice could not be loaded. Please refresh the page.
      </div>
    );
  }

  if (signed) {
    return (
      <div className={styles.notice_done}>
        <div className={styles.success_icon} aria-hidden='true'>
          ✓
        </div>
        <h1 className={styles.notice_title}>Notice signed</h1>
        <p>
          Thank you. The office will now review your signed lay-off notice and
          send it to Payroll and HR.
        </p>
        <dl className={styles.signers}>
          <dt>Last day worked</dt>
          <dd>{formatDate(signed.effective_date)}</dd>
          <dt>Signed by you</dt>
          <dd>{formatDate(signed.employee_signed_at)}</dd>
          <dt>Superintendent</dt>
          <dd>
            {signed.signed_by}, {formatDate(signed.signed_at)}
          </dd>
        </dl>
        {onDone && (
          <button type='button' className='button' onClick={onDone}>
            Continue to Firestorm
          </button>
        )}
      </div>
    );
  }

  const status = getLayoffStatus(notice.status);

  return (
    <div className={styles.notice_page}>
      {canSign && (
        <div className={styles.lock_banner} role='status'>
          <strong>Signature required.</strong> Review your lay-off notice,
          complete your part and sign it to continue using Firestorm.
        </div>
      )}

      <div className={styles.notice_heading}>
        <h1 className={styles.notice_title}>Lay-off notice</h1>
        <DocStatusTag
          status={canSign ? 'Your turn to sign' : notice.status_label}
          bgColor={status.bg}
          fontColor={status.fg}
          className={styles.status_tag}
        />
      </div>

      <dl className={styles.notice_facts}>
        <dt>Crew</dt>
        <dd>{notice.crew}</dd>
        <dt>Last day worked</dt>
        <dd>
          {formatDate(notice.effective_date) || 'Set by your superintendent'}
        </dd>
        {notice.signed_by && (
          <>
            <dt>Superintendent</dt>
            <dd>
              {notice.signed_by}, signed {formatDate(notice.signed_at)}
            </dd>
          </>
        )}
      </dl>

      <div className={styles.send_layout}>
        <section
          aria-label='Document'
          className={`${styles.picker} ${styles.document_panel}`}
        >
          <div className={styles.document_head}>
            <span>Employee Discharge Form</span>
            {preview.data && (
              <button
                type='button'
                className={`button outlined ${styles.small_button}`}
                onClick={() => openPdf(preview.data)}
              >
                Open full size
              </button>
            )}
          </div>
          {canSign && (
            <p className={styles.hint}>
              Your answers appear on the form a moment after you enter them.
            </p>
          )}
          {preview.isError && (
            <p>The document could not be loaded. Please try again.</p>
          )}
          {preview.data ? (
            <PdfDocument file={preview.data} />
          ) : (
            <div className={styles.empty}>Loading document...</div>
          )}
        </section>

        <aside
          aria-label='Your part'
          className={`${styles.summary} ${styles.sign_panel}`}
        >
          {notice.status === 1 && (
            <p>
              Your superintendent will sign this notice first and set your last
              day worked. You will then be asked to complete your part and sign.
              You can keep using Firestorm as normal until then.
            </p>
          )}
          {notice.status === 5 && (
            <p>
              This lay-off notice was cancelled. No action is needed from you.
            </p>
          )}
          {notice.status === 6 && (
            <p>
              This lay-off notice was voided because it was sent in error
              {notice.void_reason ? `: ${notice.void_reason}` : '.'} No action
              is needed from you.
            </p>
          )}
          {!canSign && ![1, 5, 6].includes(notice.status) && (
            <p>
              {notice.employee_signed_at
                ? `You signed this notice on ${formatDate(notice.employee_signed_at)}. No further action is needed.`
                : 'This notice no longer needs your signature.'}
            </p>
          )}
          {canSign && (
            <>
              <h2 className={styles.summary_title}>Confirm with employee</h2>
              <p className={styles.hint}>
                1. PPH (if applicable) will be cashed out and EDD will be
                notified of income on the date paid.
              </p>

              <fieldset className={styles.security_group}>
                <legend className={styles.field_label}>
                  2. Mailing address and phone for your W-2
                </legend>
                <TextField
                  required
                  id='address-1'
                  label='Address line 1'
                  autoComplete='address-line1'
                  maxLength={100}
                  value={form.mailing_address_line1}
                  onChange={setField('mailing_address_line1')}
                  error={shownErrors['form.mailing_address_line1']}
                />
                <TextField
                  required
                  id='address-2'
                  label='City, state, ZIP'
                  autoComplete='address-line2'
                  maxLength={100}
                  value={form.mailing_address_line2}
                  onChange={setField('mailing_address_line2')}
                  error={shownErrors['form.mailing_address_line2']}
                />
                <TextField
                  required
                  id='phone'
                  type='tel'
                  label='Phone'
                  autoComplete='tel'
                  maxLength={20}
                  value={form.phone}
                  onChange={setField('phone')}
                  error={shownErrors['form.phone']}
                />
              </fieldset>

              <RadioGroup
                required
                name='returning_next_year'
                legend='3. Planning to return next year?'
                options={YES_NO_UNKNOWN}
                value={form.returning_next_year}
                onChange={setField('returning_next_year')}
                error={shownErrors['form.returning_next_year']}
              />
              <RadioGroup
                required
                name='available_off_season'
                legend='Available off season?'
                options={YES_NO_UNKNOWN}
                value={form.available_off_season}
                onChange={setField('available_off_season')}
                error={shownErrors['form.available_off_season']}
              />

              <div>
                <RadioGroup
                  required
                  name='insurance'
                  legend='4. Insurance'
                  options={[
                    { value: 'not_enrolled', label: 'Not enrolled' },
                    { value: 'cancel', label: 'Cancel insurance' },
                    {
                      value: 'keep_deduct',
                      label: 'Keep and deduct 2 months of premiums',
                    },
                    {
                      value: 'payment_plan',
                      label: "Don't deduct, I'll set up a payment plan",
                    },
                  ]}
                  value={form.insurance}
                  onChange={setField('insurance')}
                  error={shownErrors['form.insurance']}
                />
                {form.insurance === 'cancel' && (
                  <div className={styles.sub_field}>
                    <TextField
                      required
                      id='insurance-cancel-date'
                      type='date'
                      label='Cancel insurance effective'
                      value={form.insurance_cancel_date}
                      onChange={setField('insurance_cancel_date')}
                      error={shownErrors['form.insurance_cancel_date']}
                    />
                  </div>
                )}
              </div>

              <RadioGroup
                required
                name='final_check'
                legend='5. Final check (if applicable)'
                options={[
                  {
                    value: 'direct_deposit',
                    label: 'Direct deposit (may take 2-3 extra days)',
                  },
                  {
                    value: 'pickup',
                    label: 'Pick up a paper check at the Chico office',
                  },
                ]}
                value={form.final_check}
                onChange={setField('final_check')}
                error={shownErrors['form.final_check']}
              />

              <div>
                <RadioGroup
                  required
                  name='workplace_injuries'
                  legend='6. Workplace injuries'
                  options={[
                    { value: 'none', label: 'None to report' },
                    { value: 'yes', label: 'Yes, I have one to report' },
                  ]}
                  value={form.workplace_injuries}
                  onChange={setField('workplace_injuries')}
                  error={shownErrors['form.workplace_injuries']}
                />
                {form.workplace_injuries === 'yes' && (
                  <div className={styles.sub_field}>
                    <TextField
                      required
                      id='injury-description'
                      label='Describe the injury'
                      maxLength={200}
                      value={form.injury_description}
                      onChange={setField('injury_description')}
                      error={shownErrors['form.injury_description']}
                    />
                  </div>
                )}
              </div>

              <div>
                <label htmlFor='notes' className={styles.field_label}>
                  7. Additional notes or comments (write &quot;None&quot; if
                  there are none)
                  <RequiredMark />
                </label>
                <textarea
                  id='notes'
                  rows={3}
                  maxLength={300}
                  className={`${styles.textarea} ${shownErrors['form.additional_notes'] ? styles.invalid : ''}`}
                  value={form.additional_notes}
                  onChange={(event) =>
                    setField('additional_notes')(event.target.value)
                  }
                />
                <FieldError message={shownErrors['form.additional_notes']} />
              </div>

              <SignatureField
                required
                label='Your signature'
                value={signature}
                onChange={(value) => {
                  setSignature(value);
                  setErrors((prev) => ({ ...prev, signature: undefined }));
                }}
                error={shownErrors.signature}
                savedSignature={savedSignature}
              />

              <div className={styles.confirm_row}>
                <input
                  id='acknowledge'
                  type='checkbox'
                  checked={acknowledge}
                  onChange={() => setAcknowledge(!acknowledge)}
                />
                <label htmlFor='acknowledge'>
                  I have read and received this lay-off notice.
                  <RequiredMark />
                </label>
              </div>
              <FieldError message={shownErrors.acknowledge} />
              <p className={styles.hint}>
                Signing this form does not indicate agreement but only signifies
                that you have been informed of the action.
              </p>

              {Object.values(shownErrors).some(Boolean) && (
                <div className={styles.error_text} role='alert'>
                  Some answers need attention. See the messages above.
                </div>
              )}

              <button
                type='button'
                className='button'
                onClick={submit}
                disabled={sign.isLoading}
              >
                {sign.isLoading ? 'Signing...' : 'Sign and submit'}
              </button>
            </>
          )}
        </aside>
      </div>
    </div>
  );
};

export default EmployeeNotice;
